/**
 * EXPRESSÕES ALGÉBRICAS DIGITADAS — leitura segura e comparação por avaliação.
 *
 * O aluno escreve "3n + 2", "2(n − 1) + 5" ou "4·n − 1"; o corretor precisa saber se
 * isso é o mesmo que a chave, escrita de outro jeito. Sem sistema de álgebra: as duas
 * expressões são CALCULADAS em vários valores da variável, e são equivalentes se
 * derem o mesmo em todos. Para polinômios de grau baixo, alguns valores bastam; o
 * item escolhe valores que não sejam raízes coincidentes.
 *
 * NUNCA `eval`. O texto passa por um analisador de verdade, que só conhece números,
 * uma variável, + − × ÷ ^ e parênteses. Qualquer outra coisa vira mensagem amigável.
 */

export type Expr =
  | { readonly t: 'num'; readonly v: number }
  | { readonly t: 'var' }
  | { readonly t: 'neg'; readonly a: Expr }
  | {
      readonly t: 'op';
      readonly op: '+' | '-' | '*' | '/' | '^';
      readonly a: Expr;
      readonly b: Expr;
    };

export class ErroExpressao extends Error {}

type Token =
  | { readonly k: 'num'; readonly v: number }
  | { readonly k: 'var' }
  | { readonly k: 'op'; readonly v: string }
  | { readonly k: '(' }
  | { readonly k: ')' };

/** Normaliza a escrita: menos tipográfico, ×, ÷, vírgula decimal, espaços. */
export function normalizar(texto: string): string {
  return texto
    .replace(/[−–—]/g, '-')
    .replace(/[×·∙*]/g, '*')
    .replace(/[÷:]/g, '/')
    .replace(/,/g, '.')
    .replace(/\s+/g, '');
}

function tokenizar(texto: string, variavel: string): Token[] {
  const s = normalizar(texto);
  const v = variavel.toLowerCase();
  const out: Token[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i] as string;
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < s.length && /[0-9.]/.test(s[j] as string)) j++;
      const bruto = s.slice(i, j);
      if ((bruto.match(/\./g) ?? []).length > 1)
        throw new ErroExpressao(`O número "${bruto.replace('.', ',')}" tem mais de uma vírgula.`);
      out.push({ k: 'num', v: Number(bruto) });
      i = j;
    } else if (c.toLowerCase() === v) {
      out.push({ k: 'var' });
      i++;
    } else if ('+-*/^'.includes(c)) {
      out.push({ k: 'op', v: c });
      i++;
    } else if (c === '(' || c === '[') {
      out.push({ k: '(' });
      i++;
    } else if (c === ')' || c === ']') {
      out.push({ k: ')' });
      i++;
    } else if (/[a-zA-Z]/.test(c)) {
      throw new ErroExpressao(`A letra "${c}" não é a variável desta questão: use ${variavel}.`);
    } else {
      throw new ErroExpressao(`O símbolo "${c}" não pode ser lido numa expressão.`);
    }
  }
  return out;
}

/**
 * Lê a expressão. Gramática:
 *   expr   := termo (('+'|'-') termo)*
 *   termo  := fator (('*'|'/') fator | fator-implícito)*
 *   fator  := unário ('^' fator)?
 *   unário := '-' unário | '+' unário | primário
 *   primário := número | variável | '(' expr ')'
 * A multiplicação implícita ("2n", "3(n+1)", "(n+1)(n-1)") é aceita porque é assim que
 * se escreve no caderno.
 */
export function lerExpressao(texto: string, variavel: string): Expr {
  if (normalizar(texto) === '') throw new ErroExpressao('A expressão está em branco.');
  if (texto.length > 120) throw new ErroExpressao('A expressão está longa demais.');
  const toks = tokenizar(texto, variavel);
  let i = 0;
  const olha = (): Token | undefined => toks[i];
  const come = (): Token | undefined => toks[i++];

  const primario = (): Expr => {
    const t = come();
    if (!t) throw new ErroExpressao('A expressão termina no meio de uma conta.');
    if (t.k === 'num') return { t: 'num', v: t.v };
    if (t.k === 'var') return { t: 'var' };
    if (t.k === '(') {
      const e = expr();
      if (come()?.k !== ')') throw new ErroExpressao('Falta fechar um parêntese.');
      return e;
    }
    throw new ErroExpressao(
      t.k === ')'
        ? 'Há um parêntese fechado sem ter sido aberto.'
        : 'Há dois sinais seguidos ou um sinal no lugar errado.',
    );
  };
  const unario = (): Expr => {
    const t = olha();
    if (t?.k === 'op' && t.v === '-') {
      come();
      return { t: 'neg', a: unario() };
    }
    if (t?.k === 'op' && t.v === '+') {
      come();
      return unario();
    }
    return primario();
  };
  const fator = (): Expr => {
    const base = unario();
    const t = olha();
    if (t?.k === 'op' && t.v === '^') {
      come();
      return { t: 'op', op: '^', a: base, b: fator() };
    }
    return base;
  };
  const comecaPrimario = (t: Token | undefined): boolean =>
    t !== undefined && (t.k === 'num' || t.k === 'var' || t.k === '(');
  const termo = (): Expr => {
    let e = fator();
    for (;;) {
      const t = olha();
      if (t?.k === 'op' && (t.v === '*' || t.v === '/')) {
        come();
        e = { t: 'op', op: t.v, a: e, b: fator() };
      } else if (comecaPrimario(t)) {
        e = { t: 'op', op: '*', a: e, b: fator() };
      } else {
        return e;
      }
    }
  };
  const expr = (): Expr => {
    let e = termo();
    for (;;) {
      const t = olha();
      if (t?.k === 'op' && (t.v === '+' || t.v === '-')) {
        come();
        e = { t: 'op', op: t.v, a: e, b: termo() };
      } else {
        return e;
      }
    }
  };

  const e = expr();
  if (i < toks.length)
    throw new ErroExpressao(
      toks[i]?.k === ')'
        ? 'Há um parêntese fechado sem ter sido aberto.'
        : 'Sobrou algo no fim da expressão.',
    );
  return e;
}

export function avaliar(e: Expr, x: number): number {
  switch (e.t) {
    case 'num':
      return e.v;
    case 'var':
      return x;
    case 'neg':
      return -avaliar(e.a, x);
    case 'op': {
      const a = avaliar(e.a, x);
      const b = avaliar(e.b, x);
      switch (e.op) {
        case '+':
          return a + b;
        case '-':
          return a - b;
        case '*':
          return a * b;
        case '/':
          return a / b;
        case '^':
          return a ** b;
      }
    }
  }
}

/**
 * A expressão escrita de volta, como no caderno: "3n + 2", "2(n − 1)", "4 · n". Serve
 * de prévia enquanto o aluno digita — mostra COMO o texto foi lido, sem dizer se está
 * certo.
 */
export function mostrar(e: Expr, variavel: string): string {
  const PREC: Readonly<Record<'+' | '-' | '*' | '/' | '^', number>> = {
    '+': 1,
    '-': 1,
    '*': 2,
    '/': 2,
    '^': 3,
  };
  const num = (v: number): string => {
    const s = String(Math.abs(v)).replace('.', ',');
    return v < 0 ? `−${s}` : s;
  };
  const go = (x: Expr, pai: number): string => {
    switch (x.t) {
      case 'num':
        return num(x.v);
      case 'var':
        return variavel;
      case 'neg': {
        const s = `−${go(x.a, 3)}`;
        return pai > 1 ? `(${s})` : s;
      }
      case 'op': {
        const p = PREC[x.op];
        let s: string;
        if (x.op === '*') {
          const a = go(x.a, 2);
          const b = go(x.b, 2.5);
          const junta =
            x.a.t === 'num' &&
            (x.b.t === 'var' ||
              (x.b.t === 'op' && (x.b.op === '^' || x.b.op === '+' || x.b.op === '-')));
          s = junta ? `${a}${b.startsWith('(') || x.b.t === 'var' ? b : `(${b})`}` : `${a} · ${b}`;
        } else if (x.op === '^') {
          s = `${go(x.a, 4)}^${go(x.b, 3)}`;
        } else {
          const sinal = x.op === '+' ? '+' : x.op === '-' ? '−' : '/';
          s = `${go(x.a, p)} ${sinal} ${go(x.b, p + 0.5)}`;
        }
        return p < pai ? `(${s})` : s;
      }
    }
  };
  return go(e, 0);
}

/** A expressão usa a variável? Uma constante não descreve uma sequência. */
export function usaVariavel(e: Expr): boolean {
  switch (e.t) {
    case 'num':
      return false;
    case 'var':
      return true;
    case 'neg':
      return usaVariavel(e.a);
    case 'op':
      return usaVariavel(e.a) || usaVariavel(e.b);
  }
}

/** Mesmos valores em todas as amostras (com folga de arredondamento). */
export function equivalentes(f: Expr, g: Expr, amostras: readonly number[]): boolean {
  return amostras.every((x) => {
    const a = avaliar(f, x);
    const b = avaliar(g, x);
    return (
      Number.isFinite(a) &&
      Number.isFinite(b) &&
      Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b))
    );
  });
}
